import React, { useState, useRef } from 'react';
import APIService from '../services/api';

/**
 * Remplissage du récapitulatif client — le classeur Excel fourni par le client,
 * complété par le serveur avec les candidats placés et les données de
 * l'organisation.
 *
 * Le fichier produit n'est PAS archivé côté serveur : c'est un livrable à
 * relire puis envoyer. Tout l'écran est donc construit autour de la relecture —
 * le rapport dit ce qui a été rempli, ce qui reste vide, ce qui a été posé par
 * défaut et ce que le serveur a refusé de trancher (deux salaires différents
 * selon le besoin : la cellule est laissée vide, la décision appartient à
 * l'humain). Télécharger sans lire ce rapport, c'est envoyer un classeur qu'on
 * n'a pas vérifié.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '9px',
  border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)',
  fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};
const btnFort = { ...btn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' };

const nomDe = (c) => c?.nom || c?.candidat || c?.name || `Candidat ${c?.candidate_id ?? ''}`;
const liste = (v) => (Array.isArray(v) ? v : v ? [v] : []);

/** Une ligne de détail du rapport : icône, teinte, libellé + éléments. */
function Detail({ icon, couleur, fond, titre, items }) {
  const arr = liste(items);
  if (!arr.length) return null;
  return (
    <div style={{ display: 'flex', gap: '7px', alignItems: 'flex-start', padding: '5px 8px', borderRadius: '7px', background: fond }}>
      <BI icon={icon} style={{ color: couleur, flex: 'none', marginTop: '2px', fontSize: '11px' }} />
      <div style={{ fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
        <span style={{ fontWeight: 600, color: couleur }}>{titre}</span>{' — '}
        {arr.map(x => (typeof x === 'string' ? x : x.champ || x.cellule || x.label || JSON.stringify(x))).join(', ')}
      </div>
    </div>
  );
}

export default function AoRecapSociete({ aoId, candidateIds, showNotification, onClose }) {
  const [fichier, setFichier] = useState(null);
  const [busy, setBusy] = useState(false);
  const [absent, setAbsent] = useState(false);       // route pas encore servie
  const [erreur, setErreur] = useState(null);        // message du serveur, affiché tel quel
  const [resultat, setResultat] = useState(null);    // { blob, file_name, rapport }
  const [survol, setSurvol] = useState(false);
  const fileRef = useRef(null);

  const choisir = (f) => {
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name || '')) { showNotification?.('Choisissez le classeur Excel du client (.xlsx).', 'error'); return; }
    setFichier(f); setErreur(null); setResultat(null);
  };

  const remplir = async () => {
    if (!fichier) return;
    setBusy(true); setErreur(null);
    try {
      const fd = new FormData();
      fd.append('file', fichier);
      // Tous les candidats du dossier, explicitement. Le défaut serveur — « les
      // candidats placés » — ne trouve personne tant qu'aucun statut n'est posé,
      // et le récapitulatif doit couvrir le dossier entier de toute façon.
      // Deux encodages : répétition du champ (la forme multipart usuelle) ET
      // une version JSON, pour que le serveur lise celle qu'il attend.
      if (Array.isArray(candidateIds) && candidateIds.length) {
        candidateIds.forEach(id => fd.append('candidate_ids[]', String(id)));
        fd.append('candidate_ids', JSON.stringify(candidateIds));
      }
      const r = await fetch(`${BASE}/ao/${aoId}/recap-societe`, {
        method: 'POST', headers: { Authorization: `Bearer ${APIService.getToken()}` }, body: fd,
      });
      if (r.status === 404 || r.status === 501) { setAbsent(true); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) {
        // Les 400 du serveur sont écrits pour être lus : on les affiche tels
        // quels, dans l'écran, pas en notification fugace.
        setErreur(j.error || `HTTP ${r.status}`);
        return;
      }
      // Le classeur rempli arrive en base64 : on le matérialise une fois, le
      // téléchargement et le re-téléchargement partent du même blob.
      const bin = atob(j.xlsx_base64 || '');
      const octets = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) octets[i] = bin.charCodeAt(i);
      const blob = new Blob([octets], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const nom = j.file_name || 'recapitulatif.xlsx';
      setResultat({ blob, file_name: nom, rapport: j.rapport || {} });
      telecharger(blob, nom);
    } catch (e) { setErreur(e.message || 'Erreur réseau'); }
    finally { setBusy(false); }
  };

  const telecharger = (blob, nom) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = nom; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  const rapport = resultat?.rapport || {};
  const parCandidat = liste(rapport.par_candidat);
  // Toujours présents dans le rapport : ils disent POURQUOI une colonne est
  // restée à saisir — règle inactive ou prix introuvable. Sans eux, la
  // question « pourquoi le tarif est vide » revient à chaque classeur.
  const regles = [rapport.tarifs_regle, rapport.taux_regle, rapport.pays_regle, rapport.disponibilite_regle].filter(Boolean);
  const societeManque = liste(rapport.societe?.a_completer);
  const horsFichier = liste(rapport.besoins_hors_fichier);
  const nonPlaces = liste(rapport.candidats_non_places);

  return (
    <>
      <div onClick={busy ? undefined : onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(8,9,12,0.5)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 81, width: 'min(640px,94vw)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 24px 60px rgba(8,9,12,0.4)', padding: '20px 22px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)', flex: 1 }}>
            Remplir le récapitulatif client
          </span>
          <button onClick={onClose} disabled={busy} style={{ ...btn, width: '30px', height: '30px', padding: 0, justifyContent: 'center' }}><BI icon="x-lg" style={{ fontSize: '12px' }} /></button>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '14px' }}>
          Le classeur du client, complété avec les {Array.isArray(candidateIds) && candidateIds.length ? `${candidateIds.length} candidats du dossier` : 'candidats du dossier'}.
          Le fichier produit n'est pas archivé : relisez-le, puis envoyez-le.
        </div>

        {absent ? (
          <div style={{ fontSize: '12.5px', color: 'var(--text-2)', padding: '12px 14px', borderRadius: '10px', background: 'var(--code-bg)' }}>
            Le remplissage du récapitulatif arrive côté serveur — rien n'a été envoyé.
          </div>
        ) : !resultat ? (
          <>
            <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; choisir(f); }} />
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setSurvol(true); }}
              onDragLeave={() => setSurvol(false)}
              onDrop={e => { e.preventDefault(); setSurvol(false); choisir(e.dataTransfer.files?.[0]); }}
              style={{
                border: `2px dashed ${survol ? 'var(--accent)' : 'var(--input-border)'}`,
                borderRadius: '12px', padding: '26px 18px', textAlign: 'center', cursor: 'pointer',
                background: survol ? 'var(--ghost-bg)' : 'transparent', transition: 'border-color .15s',
              }}>
              <BI icon="file-earmark-spreadsheet" style={{ fontSize: '24px', color: 'var(--text-3)' }} />
              <div style={{ marginTop: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>
                {fichier ? fichier.name : 'Déposez le classeur Excel du client'}
              </div>
              <div style={{ marginTop: '3px', fontSize: '11.5px', color: 'var(--text-3)' }}>
                {fichier ? 'Cliquez pour en choisir un autre' : 'ou cliquez pour le sélectionner (.xlsx)'}
              </div>
            </div>

            {erreur && (
              <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '9px', background: 'rgba(226,35,26,0.08)', border: '1px solid rgba(226,35,26,0.28)', fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.55 }}>
                {erreur}
              </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={remplir} disabled={!fichier || busy} style={{ ...btnFort, opacity: (!fichier || busy) ? 0.6 : 1 }}>
                <BI icon={busy ? 'arrow-repeat' : 'magic'} style={busy ? { animation: 'ao-spin .7s linear infinite' } : undefined} />
                {busy ? 'Remplissage…' : 'Remplir automatiquement'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 13px', borderRadius: '10px', background: 'rgba(45,200,110,0.10)', border: '1px solid rgba(45,200,110,0.30)' }}>
              <BI icon="check-circle-fill" style={{ color: '#16a34a', flex: 'none' }} />
              <div style={{ flex: 1, minWidth: 0, fontSize: '12.5px', color: 'var(--text-2)' }}>
                <b style={{ color: 'var(--text-strong)' }}>{resultat.file_name}</b> téléchargé — relisez-le avant envoi.
              </div>
              <button onClick={() => telecharger(resultat.blob, resultat.file_name)} style={{ ...btn, padding: '6px 11px', fontSize: '12px', flex: 'none' }}>
                <BI icon="download" /> Retélécharger
              </button>
            </div>

            {/* Les règles de remplissage, en tête : elles expliquent d'un coup
                ce que cent lignes « à saisir » ne disent pas. */}
            {regles.length > 0 && (
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {regles.map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: '7px', alignItems: 'flex-start', padding: '7px 10px', borderRadius: '8px', background: 'var(--chip-bg)' }}>
                    <BI icon="sliders" style={{ color: 'var(--text-3)', flex: 'none', marginTop: '2px', fontSize: '11px' }} />
                    <span style={{ fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>{String(r)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* La relecture guidée : ce qui manque d'abord, candidat par candidat. */}
            {societeManque.length > 0 && (
              <Detail icon="building" couleur="#b45309" fond="rgba(217,119,6,0.08)" titre="Société — à compléter" items={societeManque} />
            )}
            {parCandidat.length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {parCandidat.map((c, i) => {
                  const aCompleter = liste(c.a_completer);
                  const conflits = liste(c.conflits);
                  const parDefaut = liste(c.par_defaut);
                  const tarifsDefaut = liste(c.tarifs_par_defaut);
                  const tarifsASaisir = liste(c.tarifs_a_saisir);
                  const propre = !aCompleter.length && !conflits.length && !tarifsASaisir.length;
                  return (
                    <div key={i} style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)', flex: 1 }}>{nomDe(c)}</span>
                        {propre
                          ? <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '5px', background: 'rgba(45,200,110,0.14)', color: '#16a34a' }}>complet</span>
                          : <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '5px', background: 'rgba(217,119,6,0.14)', color: '#b45309' }}>à relire</span>}
                      </div>
                      <div style={{ marginTop: conflits.length + aCompleter.length + parDefaut.length + tarifsDefaut.length + tarifsASaisir.length ? '7px' : 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Detail icon="exclamation-octagon" couleur="var(--brand)" fond="rgba(226,35,26,0.07)"
                          titre="Conflit, cellule laissée vide" items={conflits} />
                        <Detail icon="pencil" couleur="#b45309" fond="rgba(217,119,6,0.08)"
                          titre="À compléter" items={aCompleter} />
                        <Detail icon="pencil" couleur="#b45309" fond="rgba(217,119,6,0.08)"
                          titre="Tarifs à saisir" items={tarifsASaisir} />
                        <Detail icon="sliders" couleur="var(--text-2)" fond="var(--chip-bg)"
                          titre="Posé par règle d'organisation" items={parDefaut} />
                        <Detail icon="sliders" couleur="var(--text-2)" fond="var(--chip-bg)"
                          titre="Tarif au prix max" items={tarifsDefaut} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {horsFichier.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <Detail icon="question-circle" couleur="var(--text-2)" fond="var(--chip-bg)"
                  titre="Positionnements absents du classeur" items={horsFichier} />
              </div>
            )}
            {nonPlaces.length > 0 && (
              <div style={{ marginTop: '6px' }}>
                <Detail icon="people" couleur="var(--text-2)" fond="var(--chip-bg)"
                  titre="Au-delà des 100 colonnes du classeur" items={nonPlaces.map(nomDe)} />
              </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => { setResultat(null); setFichier(null); }} style={btn}>Refaire avec un autre fichier</button>
              <button onClick={onClose} style={btnFort}>Fermer</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
